import { MicOff } from 'lucide-react';
import { DevToolsEmptyState } from 'Modules/softphone/components/SoftphoneDevTools/DevToolsEmptyState';
import type { DevicesPermissionPanelProps } from 'Modules/softphone/components/SoftphoneDevTools/DevToolsTabs/DevicesTab/DevicesPermissionPanel/index.types';
import {
  TONE,
} from 'Modules/softphone/components/SoftphoneDevTools/styles';
import React from 'react';
import { cn } from '~/lib/utils';

/**
 * DevicesPermissionPanel - Whether the browser will let the softphone hear
 *
 * Granted is one quiet line, because it is the expected state and the counts
 * are the only news in it.
 */
export const DevicesPermissionPanel: React.FC<DevicesPermissionPanelProps> = ({
  state,
  microphoneCount,
  speakerCount,
  ringerCount,
  onRequest,
  isRequesting,
}) => {
  if (state === 'granted') {
    return (
      <div
        className={cn(
          'flex items-center gap-2.5 px-3 py-2.5',
          TONE.ok.inset,
          'rounded-[10px]'
        )}
      >
        <span
          className={cn('h-1.5 w-1.5 flex-shrink-0 rounded-full', TONE.ok.dot)}
        />
        <span className="text-xs text-zinc-300">
          <span className="font-semibold text-emerald-400">Microphone granted</span> ·{' '}
          {microphoneCount} mics · {speakerCount} speakers · {ringerCount}{' '}
          ringers detected
        </span>
      </div>
    );
  }

  const isDenied = state === 'denied';

  return (
    <DevToolsEmptyState
      icon={<MicOff className="h-[21px] w-[21px]" strokeWidth={1.7} />}
      tone={isDenied ? 'bad' : 'warn'}
      title={
        isDenied
          ? 'Microphone permission denied'
          : 'Microphone permission not granted'
      }
      body={
        isDenied
          ? 'The browser is refusing the microphone. It has to be re-allowed from the site settings — the page cannot prompt again.'
          : 'Until the prompt is accepted the browser returns placeholder devices with no id, which the panel drops — so every list below stays empty.'
      }
      action={isDenied ? undefined : isRequesting ? 'Requesting…' : 'Request permission'}
      onAction={isDenied ? undefined : onRequest}
      hint="the browser remembers the choice per origin"
    />
  );
};
